import {
  useMemo,
  useEffect,
  useRef,
  useState,
  useCallback,
  DependencyList,
  useLayoutEffect,
} from 'react';
import { createResource, Resource, ResourceState, ResourceFetcher } from '../Resource';
import { useValue } from 'lib/domonda-rxjs/react/hooks';
import { deepEqual, shallowEqual } from 'fast-equals';

// Simple wrapper allowing async effects.
export function useAsyncEffect(effect: () => Promise<void | (() => void)>, deps?: DependencyList) {
  return useEffect(() => {
    const cleanupPromise = effect();
    return () => {
      cleanupPromise.then((cleanup) => cleanup && cleanup());
    };
  }, deps);
}

// Memoizes the passed value by performing a deep compare.
export function useDeepMemoOnValue<T>(value: T): T {
  const ref = useRef<T>(value);
  if (!deepEqual(ref.current, value)) {
    ref.current = value;
  }
  return ref.current;
}

// Memoizes the passed value by performing a shallow compare.
export function useShallowMemoOnValue<T>(value: T): T {
  const ref = useRef<T>(value);
  if (!shallowEqual(ref.current, value)) {
    ref.current = value;
  }
  return ref.current;
}

// Resolves the passed promise. The `promise` is retried everytime the `params` have changed.
// The function returns `[result, error | null, retry()]`. (When `result` is `undefined`, its still loading).
export function useResolve<T, P>(
  promise: (params: P) => Promise<T>,
  params: P,
): [T | undefined, Error | null, () => void] {
  const [result, setResult] = useState<T | undefined>(undefined);
  const [error, setError] = useState<Error | null>(null);

  const memoParams = useDeepMemoOnValue(params);

  let canceled = false;
  const perform = useCallback(() => {
    setResult(undefined);
    setError(null);
    promise(memoParams)
      .then((result) => {
        if (!canceled) {
          setResult(result);
        }
      })
      .catch((error) => {
        if (!canceled) {
          setError(error);
        }
      });
  }, [memoParams]);

  useLayoutEffect(() => {
    perform();
    return () => {
      canceled = true;
    };
  }, [perform]);

  return [result, error, perform];
}

/** Memoizes the passed params and calls `fetch` on the resource whenever the `params` change. */
export function useResourceUpdateParams<V, P>(resource: Resource<V, P>, params: P) {
  const memoParams = useDeepMemoOnValue(params);
  useEffect(() => {
    resource.fetch(memoParams);
  }, [resource, memoParams]);
}

/** Retrieves the resource `state` and updates it on every change. */
export function useResourceState<V, P>(resource: Resource<V, P>): ResourceState<V, P> {
  const state = useValue(() => resource.$, () => resource.state, [resource]);
  return state;
}

/** Creates the resource, re-fetches the data on params update and retrieves both the resource state and the resource itself. */
export function useCreatedResource<V, P>(
  fetcher: ResourceFetcher<V, P>,
  params: P,
  deps?: DependencyList,
): [ResourceState<V, P>, Resource<V, P>] {
  const resource = useMemo(() => createResource(fetcher, params), deps);
  useResourceUpdateParams(resource, params);
  const state = useResourceState(resource);
  return [state, resource];
}

/** Calls the given callback after the timeout has passed. The function returns a `trigger`. */
export function useTimeout(props: {
  callback: () => void;
  timeout: number;
  onTrigger?: () => void;
}): () => void {
  const { callback, timeout, onTrigger } = props;

  const timeoutRef = useRef(0);

  const trigger = useCallback(() => {
    // if there was a previous timeout, clear it
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // callback after given timeout
    timeoutRef.current = (setTimeout(() => {
      callback();
    }, timeout) as any) as number;

    if (onTrigger) {
      onTrigger();
    }
  }, [callback, timeout, onTrigger]);

  // clear the timeout on unmount, or callback change
  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    },
    [callback, timeout],
  );

  return trigger;
}

/**
 * Creates a expiring boolean flag. The hook returns: `[flag, trigger]`. When calling trigger,
 * the flag will turn `true` and after the given timout has passed, will go back to `false`.
 */
export function useExpire(timeout: number): [boolean, () => void] {
  const [flag, setFlag] = useState(false);

  const timeoutProps = useMemo(
    () => ({
      callback: () => setFlag(false),
      timeout,
      onTrigger: () => setFlag(true),
    }),
    [timeout],
  );

  const trigger = useTimeout(timeoutProps);

  return [flag, trigger];
}
